import { useEffect, useRef, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { cases } from '@/mocks/cases';

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {children}
    </span>
  );
}

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const caseData = cases.find((c) => c.id === id);

  if (!caseData) return <Navigate to="/cases" replace />;

  const currentIdx = cases.findIndex((c) => c.id === id);
  const prevCase = currentIdx > 0 ? cases[currentIdx - 1] : null;
  const nextCase = currentIdx < cases.length - 1 ? cases[currentIdx + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-0 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={caseData.coverImage}
            alt={caseData.title}
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/70 via-[#0F172A]/60 to-[#0F172A]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-10 pt-4">
            <Link
              to="/cases"
              className="text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              案例
            </Link>
            <i className="ri-arrow-right-s-line text-white/20 text-xs" />
            <span className="text-xs text-white/40 truncate" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
              {caseData.title}
            </span>
          </div>

          {/* Tag + Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{
                color: caseData.tagColor,
                backgroundColor: `${caseData.tagColor}25`,
                fontFamily: '\'Noto Sans SC\', sans-serif',
              }}
            >
              {caseData.tag}
            </span>
            {[caseData.industry, caseData.teamSize, caseData.duration].map((m) => (
              <span
                key={m}
                className="text-xs text-white/40"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                {m}
              </span>
            ))}
          </div>

          <h1
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
          >
            {caseData.title}
          </h1>
          <p
            className="text-base md:text-lg text-white/50 max-w-2xl"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
          >
            {caseData.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Left: Main Story */}
          <div className="lg:col-span-2 flex flex-col gap-16">

            {/* 1. 客户背景 */}
            <FadeIn>
              <div>
                <SectionLabel>01 · Client Background</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  客户背景
                </h2>
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100">
                  <p
                    className="text-base text-[#0F172A] font-semibold mb-4"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.client.name}
                  </p>
                  <p
                    className="text-sm text-[#64748B] leading-relaxed mb-5"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.client.desc}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-[#64748B] mb-1" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>团队规模</p>
                      <p className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{caseData.client.scale}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-100">
                      <p className="text-xs text-[#64748B] mb-1" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>技术栈</p>
                      <p className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{caseData.client.tech}</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* 2. 原问题 */}
            <FadeIn>
              <div>
                <SectionLabel>02 · The Problem</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  原问题
                </h2>
                <div className="mb-5 p-6 rounded-2xl bg-[#F97316]/5 border border-[#F97316]/15">
                  <p
                    className="text-base text-[#0F172A] leading-relaxed font-medium"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.problem.summary}
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  {caseData.problem.details.map((d, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-100">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-[#F97316]/10 flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-[#F97316] text-xs" />
                      </div>
                      <p
                        className="text-sm text-[#64748B] leading-relaxed"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {d}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* 3. 为什么难做 */}
            <FadeIn>
              <div>
                <SectionLabel>03 · Why It Was Hard</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  为什么难做
                </h2>
                <div className="mb-5 p-6 rounded-2xl bg-[#64748B]/5 border border-slate-200">
                  <p
                    className="text-base text-[#0F172A] leading-relaxed font-medium"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.whyHard.summary}
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  {caseData.whyHard.details.map((d, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-[#F8FAFC] rounded-xl p-4 border border-slate-100">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-200 flex-shrink-0 mt-0.5">
                        <span
                          className="text-xs font-bold text-[#64748B]"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {idx + 1}
                        </span>
                      </div>
                      <p
                        className="text-sm text-[#64748B] leading-relaxed"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {d}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* 4. AI 如何改变成本结构 */}
            <FadeIn>
              <div>
                <SectionLabel>04 · How AI Changed the Cost Structure</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  如何借助 AI 改变成本结构
                </h2>
                <div className="mb-6 p-6 rounded-2xl bg-[#2563EB]/5 border border-[#2563EB]/15">
                  <p
                    className="text-base text-[#0F172A] leading-relaxed font-medium"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.aiApproach.summary}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {caseData.aiApproach.steps.map((step, idx) => (
                    <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#0F172A]/5">
                          <i className={`${step.icon} text-[#0F172A] text-base`} />
                        </div>
                        <span
                          className="text-xs font-bold text-[#2563EB]/60"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          0{idx + 1}
                        </span>
                      </div>
                      <h4
                        className="text-sm font-bold text-[#0F172A] mb-2"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {step.title}
                      </h4>
                      <p
                        className="text-xs text-[#64748B] leading-relaxed"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {step.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* 5. 落地过程 */}
            <FadeIn>
              <div>
                <SectionLabel>05 · Execution</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  落地过程
                </h2>
                <div className="flex flex-col gap-4">
                  {caseData.execution.phases.map((ph, idx) => (
                    <div
                      key={idx}
                      className={`rounded-2xl p-7 border ${idx === 0 ? 'bg-[#0F172A] border-transparent' : 'bg-white border-slate-100'}`}
                    >
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-xs font-bold uppercase tracking-wider ${idx === 0 ? 'text-white/40' : 'text-[#2563EB]/50'}`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {ph.phase}
                          </span>
                          <h4
                            className={`text-base font-bold ${idx === 0 ? 'text-white' : 'text-[#0F172A]'}`}
                            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                          >
                            {ph.title}
                          </h4>
                        </div>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${idx === 0 ? 'bg-white/10 text-white/60' : 'bg-[#F97316]/10 text-[#F97316]'}`}
                          style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                        >
                          {ph.duration}
                        </span>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {ph.actions.map((action, aIdx) => (
                          <li key={aIdx} className="flex items-start gap-2">
                            <i className={`ri-check-line text-sm mt-0.5 flex-shrink-0 ${idx === 0 ? 'text-[#F97316]' : 'text-[#2563EB]'}`} />
                            <span
                              className={`text-xs leading-relaxed ${idx === 0 ? 'text-white/60' : 'text-[#64748B]'}`}
                              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                            >
                              {action}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* 6. 最终结果 */}
            <FadeIn>
              <div>
                <SectionLabel>06 · Results</SectionLabel>
                <h2
                  className="text-2xl font-bold text-[#0F172A] mb-6"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  最终结果
                </h2>
                <div className="mb-6 p-6 rounded-2xl bg-[#0F172A]">
                  <p
                    className="text-base text-white leading-relaxed font-medium"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.result.summary}
                  </p>
                </div>

                {/* Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {caseData.result.highlights.map((h, idx) => (
                    <div key={idx} className="bg-[#F8FAFC] rounded-2xl p-5 border border-slate-100">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#F97316]/10 flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-sm text-[#F97316]" />
                        </div>
                        <div>
                          <p
                            className="text-sm font-semibold text-[#0F172A] mb-1"
                            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                          >
                            {h.label}
                          </p>
                          <p
                            className="text-xs text-[#64748B] leading-relaxed"
                            style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                          >
                            {h.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quote */}
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100">
                  <i className="ri-double-quotes-l text-3xl text-slate-200 mb-4 block" />
                  <p
                    className="text-base text-[#0F172A] leading-relaxed mb-5"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.result.quote}
                  </p>
                  <p
                    className="text-sm font-semibold text-[#64748B]"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {caseData.result.quoteAuthor}
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Right: Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-5">
              {/* Quick Stats */}
              <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100">
                <p
                  className="text-xs uppercase tracking-widest text-[#64748B] mb-5"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  项目概览
                </p>
                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-building-line', label: '行业', value: caseData.industry },
                    { icon: 'ri-team-line', label: '参与规模', value: caseData.teamSize },
                    { icon: 'ri-time-line', label: '项目周期', value: caseData.duration },
                    { icon: 'ri-price-tag-3-line', label: '类型', value: caseData.tag },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-100">
                        <i className={`${item.icon} text-[#64748B] text-sm`} />
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{item.label}</p>
                        <p className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Results */}
              <div className="bg-[#0F172A] rounded-2xl p-6">
                <p
                  className="text-xs uppercase tracking-widest text-white/40 mb-5"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  核心成果
                </p>
                <div className="flex flex-col gap-3">
                  {caseData.result.highlights.map((h) => (
                    <div key={h.label} className="flex items-start gap-2.5">
                      <i className="ri-check-line text-sm text-[#F97316] mt-0.5 flex-shrink-0" />
                      <span
                        className="text-xs text-white/70 leading-relaxed"
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        <strong className="text-white font-semibold">{h.label}</strong> · {h.desc}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <p
                  className="text-sm font-bold text-[#0F172A] mb-2"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  你的项目有类似挑战？
                </p>
                <p
                  className="text-xs text-[#64748B] leading-relaxed mb-5"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  预约一次免费交流，我们帮你判断 AI 能带来多大改变。
                </p>
                <Link
                  to="/#contact-section"
                  className="block w-full py-3 rounded-xl bg-[#F97316] text-white text-sm font-semibold text-center whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  预约交流
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prev / Next Navigation */}
      {(prevCase || nextCase) && (
        <section className="border-t border-slate-100 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {prevCase ? (
                <Link
                  to={`/cases/${prevCase.id}`}
                  className="group flex items-center gap-4 bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#2563EB]/20 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-100 group-hover:border-[#0F172A] transition-colors flex-shrink-0">
                    <i className="ri-arrow-left-line text-[#64748B] group-hover:text-[#0F172A] transition-colors" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#64748B] mb-1" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>上一个案例</p>
                    <p className="text-sm font-semibold text-[#0F172A] truncate" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{prevCase.title}</p>
                  </div>
                </Link>
              ) : <div />}

              {nextCase ? (
                <Link
                  to={`/cases/${nextCase.id}`}
                  className="group flex items-center justify-end gap-4 bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#2563EB]/20 transition-all cursor-pointer"
                >
                  <div className="min-w-0 text-right">
                    <p className="text-xs text-[#64748B] mb-1" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>下一个案例</p>
                    <p className="text-sm font-semibold text-[#0F172A] truncate" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{nextCase.title}</p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-100 group-hover:border-[#0F172A] transition-colors flex-shrink-0">
                    <i className="ri-arrow-right-line text-[#64748B] group-hover:text-[#0F172A] transition-colors" />
                  </div>
                </Link>
              ) : <div />}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
